import {
  createSelector,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { SitumApiPermissionLevel } from "@situm/sdk-js";

import type { RootState } from "../../app/store";
import type { Organization, PoiCategories } from "../../libs/models";
import type { Action, StylesMapLabelsState } from "../config/types";
import { type DotNotationKeys, genericGetProperty } from "../utils/genericity";
import { safeLocalStorage } from "../utils/utils";
import {
  AccessibleColorThemes,
  type AllColorThemes,
  ColorThemes,
  DARK_ACCESSIBLE_THEME,
  DEFAULT_THEME,
  LIGHT_ACCESSIBLE_THEME,
} from "./constants";

export interface WindowDimensions {
  height: number;
  width: number;
}

export enum AppUI {
  /**
   * Bare map mode intended for embedding: only the map and its POIs are shown,
   * without any application UI (panels, toolbars, banners, etc.).
   * A dedicated center marker can be used to pick locations.
   */
  locationPicker = "locationPicker",
  /**
   * Explore mode: when the app is in explore mode, the user can navigate through the map and see the POIs.
   * This is the default mode when the app is opened.
   */
  explore = "explore",
  /**
   * Directions mode: app mode for displaying statis routes.
   */
  directions = "directions",
  /**
   * Steps mode: app mode showing the step-by-step list of directions
   * for the current route, without live turn-by-turn navigation.
   */
  steps = "steps",
  /**
   * Navigation mode: app mode used during active turn-by-turn navigation
   * using the user's live location.
   */
  navigation = "navigation",
  /**
   * Calibration mode: internal app mode used to interact with calibrations.
   */
  calibration = "calibration",
}

export enum EditUI {
  overlayLayers = "overlays",
  baseLayers = "baseLayers",
  baseLayersTheme = "baseLayersTheme",
  settings = "settings",
  paths = "paths",
}

export enum ARStatusTypes {
  shown = "shown",
  started = "started",
  loading = "loading",
  stopped = "stopped",
}

export enum AppMessageLevel {
  ERROR = "error",
  WARNING = "warning",
  SUCCESS = "success",
}

export enum StaticsCacheStatus {
  init = "init",
  checking = "checking",
  failed = "failed",
  success = "success",
}

export type AppMessage = {
  id: string;
  message: string;
  level: AppMessageLevel;
  timeout?: number;
  cancellable?: boolean;
  persistent?: boolean;
};

export interface PositionTheme {
  iconColor: string;
  dotColor: string;
  outdoorDotColor: string;
  accuracyColor: string;
  iconBorderColor?: string;
  iconBorderWidth?: number;
  accuracyBorderColor?: string;
  accuracyBorderWidth?: number;
}

export interface RouteIndicationsTheme {
  borderColor: string;
  backgroundColor: string;
  iconColor: string;
  size: number;
}

export interface RouteTheme {
  pathColor: string;
  pathDotSize: number;
  pathDotGap: number;
  showArrow: boolean;
  arrowColor?: string;
  pathUnselectedColor?: string;
  pathAnimationColor?: string;
  borderColor?: string;
  borderWidth?: number;
  indications: RouteIndicationsTheme;
}

export interface PoiTheme {
  borderColor: string;
}

export interface Theme {
  brandColor: string;
  primaryColor: string;
  primaryFontColor: string;
  secondaryColor: string;
  secondaryFontColor: string | null;
  warningColor: string;
  iconColor: string;
  position: PositionTheme;
  route: RouteTheme;
  pois: PoiTheme;
  backgroundColor: string;
  labels: StylesMapLabelsState;
  accentLabels: StylesMapLabelsState;
}

export type HostColorScheme = "light" | "dark";

export interface SliceProps {
  currentOrganization: Organization | null;
  selectedTheme: Exclude<AllColorThemes, "system">;
  hostColorSchemePreference: HostColorScheme | null;
  error: any;
  messages: AppMessage[];
  loading: boolean;
  mapLoaded: boolean;
  poiCategories: PoiCategories | null;
  initialWindowSize: WindowDimensions;
  online: boolean;
  staticsCacheStatus: StaticsCacheStatus;
  mapboxAvailable: boolean;
  isServiceWorkerRegistered: boolean;
  arStatus: ARStatusTypes;
  ticketsModalOpen: boolean;
  outsideBuildingModalOpen: boolean;
  userSettingsPanelOpen: boolean;
  feedbackModalOpen: boolean;
  uiMode: AppUI;
  editMode?: EditUI;
  ticketsSelected: string[];
  theme: Theme;
  settings: {
    selectedSectionsStack: {
      title: string;
      section: React.ReactNode;
    }[];
    filterText: string;
  };
  topBannerHeight: number;
  actions: {
    showGeofencePopups: boolean;
    actions: Action[];
  };
  apiPermissionLevel: SitumApiPermissionLevel | undefined;
  authReady: boolean;
  locationPickerInitialPosition?:
    | {
        latitude: number;
        longitude: number;
        floorIdentifier: number;
      }
    | undefined;
}

const initialState: SliceProps = {
  actions: {
    actions: [],
    showGeofencePopups: true,
  },
  apiPermissionLevel: undefined,
  arStatus: ARStatusTypes.stopped,
  authReady: false,
  currentOrganization: null,
  error: null,
  feedbackModalOpen: false,
  hostColorSchemePreference: null,
  initialWindowSize: {
    height: 0,
    width: 0,
  },
  isServiceWorkerRegistered: false,
  loading: true,
  locationPickerInitialPosition: undefined,
  mapboxAvailable: true,
  mapLoaded: false,
  messages: [],
  online: true,
  outsideBuildingModalOpen: false,
  poiCategories: null,
  selectedTheme: ColorThemes.light,
  settings: {
    filterText: "",
    selectedSectionsStack: [],
  },
  staticsCacheStatus: StaticsCacheStatus.init,
  theme: DEFAULT_THEME,
  ticketsModalOpen: false,
  ticketsSelected:
    safeLocalStorage.getItem("tickets") &&
    JSON.parse(safeLocalStorage.getItem("tickets") || "") instanceof Array
      ? (JSON.parse(safeLocalStorage.getItem("tickets") || "") as string[])
      : [],
  topBannerHeight: 0,
  uiMode: AppUI.explore,
  userSettingsPanelOpen: false,
};

export const AppSlice = createSlice({
  initialState,
  name: "app",
  reducers: {
    addMessage: (state, action: PayloadAction<AppMessage>) => {
      state.messages = [
        ...state.messages.filter((e) => e.id != action.payload.id),
        action.payload,
      ];
    },
    clearSettingSectionStack: (state: SliceProps) => {
      state.settings.selectedSectionsStack = [];
    },
    popCurrentSetttingsSection: (state: SliceProps) => {
      state.settings.selectedSectionsStack.pop();
    },
    pushCurrentSetttingsSection: (
      state: SliceProps,
      action: PayloadAction<any>,
    ) => {
      state.settings.selectedSectionsStack.push(action.payload);
    },
    removeMessage: (state, action: PayloadAction<AppMessage["id"]>) => {
      state.messages = state.messages.filter((e) => e.id != action.payload);
    },
    setActions: (
      state: SliceProps,
      action: PayloadAction<SliceProps["actions"]["actions"]>,
    ) => {
      state.actions.actions = action.payload;
    },
    setApiPermissionLevel: (
      state: SliceProps,
      action: PayloadAction<SliceProps["apiPermissionLevel"]>,
    ) => {
      state.apiPermissionLevel = action.payload;
    },
    setARStatus: (
      state: SliceProps,
      action: PayloadAction<SliceProps["arStatus"]>,
    ) => {
      state.arStatus = action.payload;
    },
    setAuthReady(
      state: SliceProps,
      action: PayloadAction<SliceProps["authReady"]>,
    ) {
      state.authReady = action.payload;
    },
    // Sets the current organization value

    setCurrentOrganization: (state: SliceProps, action: PayloadAction<any>) => {
      state.currentOrganization = action.payload;
    },
    setDarkAccessibleTheme: (state: SliceProps) => {
      state.theme = DARK_ACCESSIBLE_THEME;
    },
    setEditMode: (
      state: SliceProps,
      action: PayloadAction<EditUI | undefined>,
    ) => {
      state.editMode = action.payload;
    },
    setError: (
      state: SliceProps,
      action: PayloadAction<SliceProps["error"]>,
    ) => {
      state.error = action.payload;
    },
    setFeedbackModalOpen: (
      state: SliceProps,
      action: PayloadAction<SliceProps["feedbackModalOpen"]>,
    ) => {
      state.feedbackModalOpen = action.payload;
    },
    setHostColorSchemePreference: (
      state: SliceProps,
      action: PayloadAction<SliceProps["hostColorSchemePreference"]>,
    ) => {
      state.hostColorSchemePreference = action.payload;
    },
    setInitialWindowSize: (
      state: SliceProps,
      action: PayloadAction<WindowDimensions>,
    ) => {
      state.initialWindowSize = action.payload;
    },
    setIsServiceWorkerRegistered(
      state: SliceProps,
      action: PayloadAction<SliceProps["isServiceWorkerRegistered"]>,
    ) {
      state.isServiceWorkerRegistered = action.payload;
    },
    setLightAccessibleTheme: (state: SliceProps) => {
      state.theme = LIGHT_ACCESSIBLE_THEME;
    },

    // Sets the loading state of the app
    setLoading: (state: SliceProps, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setLocationPickerInitialPosition: (
      state: SliceProps,
      action: PayloadAction<SliceProps["locationPickerInitialPosition"]>,
    ) => {
      state.locationPickerInitialPosition = action.payload;
    },
    setMapboxAvailable: (
      state: SliceProps,
      action: PayloadAction<SliceProps["mapboxAvailable"]>,
    ) => {
      state.mapboxAvailable = action.payload;
    },
    setMapLoaded: (
      state: SliceProps,
      action: PayloadAction<SliceProps["mapLoaded"]>,
    ) => {
      state.mapLoaded = action.payload;
    },
    setOnline: (
      state: SliceProps,
      action: PayloadAction<SliceProps["online"]>,
    ) => {
      state.online = action.payload;
    },
    setOutsideBuildingModalOpen: (
      state: SliceProps,
      action: PayloadAction<SliceProps["outsideBuildingModalOpen"]>,
    ) => {
      state.outsideBuildingModalOpen = action.payload;
    },
    // Sets the categories array values
    setPoiCategories: (
      state: SliceProps,
      action: PayloadAction<PoiCategories>,
    ) => {
      state.poiCategories = action.payload;
    },
    setSelectedTheme: (
      state: SliceProps,
      action: PayloadAction<SliceProps["selectedTheme"]>,
    ) => {
      state.selectedTheme = action.payload;
    },
    setSettingsFilterText: (
      state: SliceProps,
      action: PayloadAction<SliceProps["settings"]["filterText"]>,
    ) => {
      state.settings.filterText = action.payload;
    },
    setShowGeofencePopups: (
      state: SliceProps,
      action: PayloadAction<SliceProps["actions"]["showGeofencePopups"]>,
    ) => {
      state.actions.showGeofencePopups = action.payload;
    },
    setStaticCacheStatus: (
      state: SliceProps,
      action: PayloadAction<SliceProps["staticsCacheStatus"]>,
    ) => {
      state.staticsCacheStatus = action.payload;
    },
    setTheme: (
      state: SliceProps,
      action: PayloadAction<SliceProps["theme"]>,
    ) => {
      state.theme = action.payload;
    },
    setTicketsModalOpen: (
      state: SliceProps,
      action: PayloadAction<SliceProps["ticketsModalOpen"]>,
    ) => {
      state.ticketsModalOpen = action.payload;
    },
    setTicketsSelected: (
      state: SliceProps,
      action: PayloadAction<SliceProps["ticketsSelected"]>,
    ) => {
      state.ticketsSelected = action.payload;
      safeLocalStorage.setItem("tickets", action.payload);
    },
    setTopBannerHeight: (
      state: SliceProps,
      action: PayloadAction<SliceProps["topBannerHeight"]>,
    ) => {
      state.topBannerHeight = action.payload;
    },

    setUIMode: (state: SliceProps, action: PayloadAction<AppUI>) => {
      state.uiMode = action.payload;
    },
    setUserSettingsPanelOpen: (
      state: SliceProps,
      action: PayloadAction<SliceProps["userSettingsPanelOpen"]>,
    ) => {
      state.userSettingsPanelOpen = action.payload;
    },

    toggleEditMode: (
      state: SliceProps,
      action: PayloadAction<EditUI | undefined>,
    ) => {
      if (state.editMode === action.payload) {
        state.editMode = undefined;
      } else {
        state.editMode = action.payload;
      }
    },
    toggleUserSettingsPanelOpen: (
      state: SliceProps,
      _action: PayloadAction<SliceProps["userSettingsPanelOpen"]>,
    ) => {
      const prevValue = state.userSettingsPanelOpen;
      state.userSettingsPanelOpen = !prevValue;
    },
  },
});

// Selectors
const sliceData = (state: RootState) => state.app;

/**
 * Returns the current organization

 * @returns any
 */
export const selectCurrentOrganization = createSelector(sliceData, (item) => {
  return item.currentOrganization;
});

/**
 * Returns what theme is the app using
 */
export const selectSelectedTheme = createSelector(sliceData, (item) => {
  return item.selectedTheme;
});

export const selectHostColorSchemePreference = createSelector(
  sliceData,
  (item) => item.hostColorSchemePreference,
);

/**
 * Returns wether or not the app is using an accessible mode
 */
export const selectIsThemeAccessible = createSelector(sliceData, (item) => {
  return Object.values(AccessibleColorThemes).includes(item.selectedTheme);
});

/**
 * Returns the error state
 * @returns Exception
 */
export const selectError = createSelector(sliceData, (item) => {
  return item.error;
});
/**
 * Returns the app ui mode
 * @returns Exception
 */
export const selectUIMode = createSelector(sliceData, (item) => {
  return item.uiMode;
});

/**
 * Returns the app edit mode if available
 */
export const selectEditMode = createSelector(sliceData, (item) => {
  return item.editMode;
});

/**
 * Returns the current loading state of the app
 * @returns Boolean
 */
export const selectLoading = createSelector(sliceData, (item) => {
  return item.loading;
});

export const selectPoiCategories = createSelector(sliceData, (item) => {
  return item.poiCategories;
});

export const selectInitialWindowSize = createSelector(sliceData, (item) => {
  return item.initialWindowSize;
});

export const selectOnline = createSelector(sliceData, (item) => {
  return item.online;
});

export const selectStaticsCacheStatus = createSelector(sliceData, (item) => {
  return item.staticsCacheStatus;
});

export const selectMapboxAvailable = createSelector(sliceData, (item) => {
  return item.mapboxAvailable;
});

export const selectMapLoaded = createSelector(sliceData, (item) => {
  return item.mapLoaded;
});

export const selectARStatus = createSelector(sliceData, (item) => {
  return item.arStatus;
});

export const selectTicketsModalOpen = createSelector(sliceData, (item) => {
  return item.ticketsModalOpen;
});

export const selectTicketsSelected = createSelector(sliceData, (item) => {
  return item.ticketsSelected;
});

export const selectAppMessages = createSelector(
  sliceData,
  (item) => item.messages,
);

export const selectTopBannerHeight = createSelector(
  sliceData,
  (item) => item.topBannerHeight,
);

const storeData = (state: RootState) => state;

export const selectConfiguredPrimaryColor = createSelector(
  [storeData, selectSelectedTheme],
  (item, selectedTheme) => {
    const useDarkTheme = selectedTheme === ColorThemes.dark;
    return useDarkTheme
      ? item.config.styles.theme.dark_mode.primary_color
      : item.config.styles.theme.light_mode.primary_color;
  },
);

export const selectThemeItem =
  <T extends DotNotationKeys<Theme>>(key: T) =>
  (state: RootState) =>
    genericGetProperty(state.app.theme, key);

export const selectCurrentSettingsSection = createSelector(
  sliceData,
  (item) => {
    if (item.settings.selectedSectionsStack.length == 0) {
      return {
        section: null,
        title: null,
      };
    }
    return item.settings.selectedSectionsStack[
      item.settings.selectedSectionsStack.length - 1
    ];
  },
);

export const selectAllSelectedSections = createSelector(sliceData, (item) => {
  return item.settings.selectedSectionsStack;
});

export const selectActions = createSelector(sliceData, (item) => {
  return item.actions.actions;
});

export const selectShowGeofencePopups = createSelector(sliceData, (item) => {
  return item.actions.showGeofencePopups;
});

export const selectUserSettingsPanelOpen = createSelector(sliceData, (item) => {
  return item.userSettingsPanelOpen;
});

export const selectOutsideBuildingModalOpen = createSelector(
  sliceData,
  (item) => {
    return item.outsideBuildingModalOpen;
  },
);

export const selectFeedbackModalOpen = createSelector(sliceData, (item) => {
  return item.feedbackModalOpen;
});
export const selectSettingsFilterText = createSelector(sliceData, (item) => {
  return item.settings.filterText;
});

export const selectApiPermissionLevel = createSelector(sliceData, (item) => {
  return item.apiPermissionLevel;
});

export const selectApiPermissionCanWrite = createSelector(sliceData, (item) => {
  return [
    SitumApiPermissionLevel.CARTOGRAPHY_READ_WRITE,
    SitumApiPermissionLevel.READ_WRITE,
  ].includes(item.apiPermissionLevel);
});
export const selectAuthReady = createSelector(sliceData, (item) => {
  return item.authReady;
});

export const selectIsServiceWorkerRegistered = createSelector(
  sliceData,
  (item) => {
    return item.isServiceWorkerRegistered;
  },
);

export const selectIsCartographyEditorOpen = createSelector(
  sliceData,
  (item) => {
    return (
      item.editMode !== undefined &&
      item.editMode !== null &&
      item.editMode !== EditUI.settings
    );
  },
);

export const selectLocationPickerInitialPosition = createSelector(
  sliceData,
  (item) => item.locationPickerInitialPosition,
);

// Action creators are generated for each case reducer function
export const {
  setCurrentOrganization,
  setSelectedTheme,
  setHostColorSchemePreference,
  setError,
  setInitialWindowSize,
  setLoading,
  setMapboxAvailable,
  setMapLoaded,
  setOnline,
  setPoiCategories,
  setUIMode,
  setEditMode,
  toggleEditMode,
  setARStatus,
  setOutsideBuildingModalOpen,
  setFeedbackModalOpen,
  setTicketsModalOpen,
  setTicketsSelected,
  addMessage,
  removeMessage,
  setTheme,
  setDarkAccessibleTheme,
  setLightAccessibleTheme,
  pushCurrentSetttingsSection,
  popCurrentSetttingsSection,
  setTopBannerHeight,
  setShowGeofencePopups,
  setActions,
  setSettingsFilterText,
  clearSettingSectionStack,
  setApiPermissionLevel,
  setUserSettingsPanelOpen,
  toggleUserSettingsPanelOpen,
  setAuthReady,
  setIsServiceWorkerRegistered,
  setStaticCacheStatus,
  setLocationPickerInitialPosition,
} = AppSlice.actions;

export default AppSlice.reducer;
